import { jsonInit } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  creativeSchema,
  creativesPageSchema,
  serializeCreativesFilter,
  type CreativeMutationPayload,
  type CreativesFilter,
} from './model';

type CreativesClientOptions = {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
  demand?: boolean;
};

const creativeCreateResponseSchema = z.unknown();

export function createCreativesClient({
  config,
  accessToken,
  fetcher,
  demand = false,
}: CreativesClientOptions) {
  const prefix = demand ? '' : '/campaign';
  const api = createShigoApiClient(config, accessToken, fetcher);
  return {
    list(filter: CreativesFilter) {
      return api.request({
        path: `${prefix}/creatives/${encodeURIComponent(filter.brandId)}?${serializeCreativesFilter(filter)}`,
        schema: creativesPageSchema,
      });
    },
    detail(brandId: string, creativeId: string) {
      return api.request({
        path: `${prefix}/creatives/${encodeURIComponent(brandId)}/${encodeURIComponent(creativeId)}`,
        schema: creativeSchema,
      });
    },
    create(brandId: string, payload: CreativeMutationPayload, file?: File | null) {
      return api.request({
        path: `${prefix}/creatives/${encodeURIComponent(brandId)}`,
        schema: creativeCreateResponseSchema,
        init: { method: 'POST', body: creativeFormData(payload, file) },
      });
    },
    update(
      brandId: string,
      creativeId: string,
      payload: CreativeMutationPayload,
      file?: File | null,
    ) {
      return api.request({
        path: `${prefix}/creatives/${encodeURIComponent(brandId)}/${encodeURIComponent(creativeId)}`,
        schema: creativeSchema,
        init: file
          ? { method: 'PUT', body: creativeFormData(payload, file) }
          : jsonInit('PUT', payload),
      });
    },
    duplicate(brandId: string, creativeId: string) {
      return api.request({
        path: `${prefix}/creatives/${encodeURIComponent(brandId)}/${encodeURIComponent(creativeId)}/duplicate`,
        schema: creativeSchema,
        init: { method: 'POST' },
      });
    },
  };
}

function creativeFormData(payload: CreativeMutationPayload, file?: File | null) {
  const body = new FormData();
  body.append('createRequest', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  if (file) body.append('file', file, file.name);
  return body;
}
